"use client";

import React, { useEffect, useState } from "react";
import { PagePayoutSchema } from "@/sanity/pages/PagePayoutTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";

import laurels from "@/public/payout/laurels.svg";
import ipad from "@/public/global/ipad.png";
import { PageAssetsSchema } from "@/sanity/pages/PageAssetsTemplate";
import { motion } from "framer-motion";
import ChipSerif from "@/components/global/ui/ChipSerif";
import bulletIcon from "@/public/assets/bulletIcon.svg";

export default function RewardsSection({
  data,
  className,
}: {
  data: PageAssetsSchema;
  className?: string;
}) {
  const bgCards: { bgColor: string }[] = [
    { bgColor: "#85D0A6" },
    { bgColor: "#93BCDB" },
    { bgColor: "#FFF7E2" },
  ];
  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5] hidden lg:block",
              index >= 2 && index <= 4
                ? "border-l-white"
                : "border-l-[#E5E5E5]",
            )}
          />
        ))}
      </div>

      <div className="container px-[3rem] lg:px-0 max-w-none flex flex-col items-start lg:items-center pb-[8rem] lg:pt-[8rem] lg:pb-[2rem] relative">
        {data?.rewardsChip && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <ChipSerif text={data.rewardsChip} className="text-black/70 mb-0" />
          </motion.div>
        )}

        <motion.div
          className="richtext richtext-start lg:richtext-center prose-headings:text-[#103037] prose-p:text-[#103037]/70 prose-p:text-[2.25rem] lg:prose-p:text-[2rem] prose-p:w-[min(59.75rem,100%)] prose-strong:text-white [text-wrap-style:pretty]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {data?.rewardsText && (
            <PortableText
              value={data?.rewardsText}
              components={richTextComponent}
            />
          )}
        </motion.div>

        {data.rewardsList && (
          <div className="w-[min(230rem,100%)] grid grid-cols-1 lg:grid-cols-3 gap-[2rem] mt-[4rem] lg:mt-[6rem]">
            {data.rewardsList.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                key={"whatItem" + index}
                className="rounded-[3rem] flex flex-col lg:flex-row gap-[2rem] p-[2rem] lg:p-[1rem]"
                style={{ backgroundColor: bgCards[index % 3].bgColor }}
              >
                <Image
                  src={item.image}
                  alt="image"
                  className="!w-[8rem] lg:!w-[20rem] shrink-0 rounded-[2.5rem] hidden lg:block"
                />
                <Image
                  src={item.imageMobile}
                  alt="image"
                  className="!w-[10.5rem] shrink-0 rounded-[2.5rem] lg:hidden"
                />

                <div className="lg:px-[4rem] lg:py-[3rem] flex flex-col items-center justify-center">
                  <motion.div
                    className="richtext richtext-start
                    prose-headings:text-[#103037] prose-headings:text-[3rem] lg:prose-headings:text-[3.5rem] prose-headings:mb-[1rem]
                    prose-p:text-[#103037]/70 prose-p:text-[2.25rem] prose-p:mb-0 [text-wrap-style:pretty]
                  [&_img]:hidden prose-ul:list-disc prose-li:marker:text-[#103037]/70 prose-li:list-item prose-li:text-[2.25rem] [&_li>span>span]:!font-geist"
                  >
                    {item.text && (
                      <PortableText
                        value={item.text}
                        components={richTextComponent}
                      />
                    )}
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
